import { sumBy } from 'lodash';
import { useQuery } from 'react-query';
import { authFetch } from '../../../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../../../CommonUtils/urlWithParams';
import { useClientId } from '../../../../../_hooks/useClientId';
import { useFpId } from '../../../../hooks/useFpId';
import { queryKeys } from './queryKeys';

export const ASSET_BUCKETING_DISABLE_ERROR = 'Asset Bucketing is not enabled.';

interface YearOption {
  label: string;
  value: number;
  subLabel: string[];
}

interface Transaction {
  label: string;
  value: number;
}

export interface HoldingDTO {
  id: string;
  ticker: string;
  name: string;
  value: number;
}

export interface AssetDTO {
  id: string;
  name: string;
  institutionName?: string;
  isHoldingsBucketInheritedFromAsset: boolean;
  holdings: HoldingDTO[];
  displayValue: number | null;
  networthValue: number;
}

export interface AssetCategoryDTO {
  assets: AssetDTO[];
  chartValue: number;
  displayValue: number | null;
  excludedFromNetworth: boolean;
  label: string;
  networthValue: number;
  transactions: Transaction[];
  type: string;
}

interface BucketDTO {
  title: string;
  description: string;
  categories: AssetCategoryDTO[];
}

export interface BucketItem {
  name: string;
  description?: string;
  value: number;
  assets: AssetDTO[];
  transactions: Transaction[];
  holdings: HoldingDTO[];
  displayValue: number | null;
  networthValue: number;
  chartValue: number;
  isHoldingsBucketInheritedFromAsset: boolean;
}

interface Bucket extends Omit<BucketDTO, 'categories'> {
  items: BucketItem[];
  value: number;
  hasExcludedFromNetworth: boolean;
}

export interface AssetBucketingResultsDTO {
  allowAssetBucketingCustomization: boolean;
  buckets: BucketDTO[];
  description: string;
  isDefaultAssetBucketingLayout: boolean;
  netWorth: number;
  title: string;
  year: number;
  yearOptions: YearOption[];
}

export interface AssetBucketingResults extends Omit<AssetBucketingResultsDTO, 'buckets'> {
  buckets: Bucket[];
}

const fetchAssetBucketingResults = async (
  clientId: number,
  fpId: string,
  year: number,
): Promise<AssetBucketingResults> => {
  const data: AssetBucketingResultsDTO = await authFetch(
    urlWithParams('/Consumer/AssetBucketingResults', {
      clientId,
      fpId,
      year,
    }),
  );

  const buckets = data.buckets.map(bucket => {
    const categories = bucket.categories.map(assetCategory => ({
      name: `${assetCategory.label}${assetCategory.excludedFromNetworth ? '*' : ''}`,
      assets: assetCategory.assets,
      transactions: assetCategory.transactions,
      holdings: assetCategory.assets.map(asset => asset.holdings).flat(),
      isHoldingsBucketInheritedFromAsset: assetCategory.assets.some(
        asset => asset.isHoldingsBucketInheritedFromAsset,
      ),
      hasExcludedFromNetworth: assetCategory.excludedFromNetworth,
      value: assetCategory.networthValue,
      networthValue: assetCategory.networthValue,
      displayValue: assetCategory.displayValue,
      chartValue: assetCategory.chartValue,
    }));

    return {
      title: bucket.title,
      description: bucket.description,
      value: sumBy(categories, category => category.networthValue),
      chartValue: sumBy(categories, category => category.chartValue),
      items: categories,
      hasExcludedFromNetworth: categories.some(category => category.hasExcludedFromNetworth),
    };
  });

  return {
    ...data,
    buckets,
    netWorth: sumBy(buckets, bucket => bucket.value),
  };
};

export const useAssetBucketingResultsQuery = (year: number) => {
  const clientId = useClientId();

  const fpId = useFpId();

  return useQuery(
    queryKeys.AssetBucketing(clientId, fpId, year),

    () => fetchAssetBucketingResults(clientId, fpId, year),

    {
      enabled: !!fpId,
      cacheTime: 0,

      retry(failureCount, err) {
        // Do not retry when PLD is disabled
        return !(err.message === ASSET_BUCKETING_DISABLE_ERROR || failureCount > 2);
      },
    },
  );
};
